import { FC } from 'react';

import Heading from 'molecules/Heading';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import { HeroWithImageConfigProps } from 'components/Hero/config';
import HeroImages from 'components/Hero/variants/TrustBar/HeroImages';

const HeroSplitLottie: FC<HeroWithImageConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const {
    featuredImage,
    featuredImageMobile,
    lottieDesktop,
    lottieMobile,
    lottieLoop,
    lottieAutoplay,
    lottieType,
    leftGradientColor,
    rightGradientColor,
    heroStats,
    as,
  } = rest;

  const loop = lottieLoop ?? true;
  const autoplay = lottieAutoplay ?? true;
  const type = lottieType ?? 'json';

  const desktopLottieUrl = lottieDesktop as string | undefined;
  const mobileLottieUrl = lottieMobile as string | undefined;

  const desktopImageUrl = featuredImage?.file?.url as string | undefined;
  const mobileImageUrl = featuredImageMobile?.file?.url as string | undefined;

  const hasLottie = !!desktopLottieUrl;
  const hasImages = !!desktopImageUrl;
  const hasMedia = hasLottie || hasImages;

  const stats = (heroStats || []).filter(stat => stat.value || stat.description);

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col items-center justify-start gap-8 sm:gap-16 lg:flex-row lg:items-center lg:gap-0">
        {/*
          54% resolves to 657px inside the 1216px container. The homepage headline
          only breaks three lines deep between 638px (where "Workflows, and Agents"
          stops wrapping) and 675px (where "Integrations, Workflows," starts fitting
          on line one), so 52% left it four lines long and anything much wider
          reflows it again. Both bounds measured against the live page.
        */}
        <div className="flex w-full max-w-screen-sm flex-col gap-6 lg:z-20 lg:w-[54%] lg:max-w-none">
          <Heading {...rest} alignment="left" size="lg" as={as || 'h1'} animateOnMount>
            {/* 84px columns on a 24px gutter reproduce the 108px pitch of the mockup. */}
            {stats.length > 0 && (
              <div className="mt-6 grid w-full max-w-[300px] grid-cols-3 items-start gap-x-6 gap-y-4">
                {stats.map(stat => (
                  <div key={`${stat.value}-${stat.description}`} className="flex flex-col text-center">
                    <span className="font-spaceGrotesk text-display-md text-primary-300">{stat.value}</span>
                    <span className="text-xs text-gray-900 dark:text-white">{stat.description}</span>
                  </div>
                ))}
              </div>
            )}
          </Heading>
        </div>

        {hasMedia && (
          // 46% + the 115% inner scale below keeps the artwork the same rendered
          // size it had at 48% + 110%, so widening the text column does not shrink it.
          <div className="relative w-full lg:h-[600px] lg:w-[46%]">
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div
                className="absolute -left-1/4 top-1/2 aspect-square w-[130%] -translate-y-1/2"
                style={{
                  backgroundImage: `radial-gradient(closest-side, ${leftGradientColor || '#0a2f5d'} 15%, transparent 80%)`,
                }}
              />
              <div
                className="absolute -right-1/4 top-1/2 aspect-square w-[130%] -translate-y-1/2"
                style={{
                  backgroundImage: `radial-gradient(closest-side, ${rightGradientColor || '#09676f'} 15%, transparent 80%)`,
                }}
              />
            </div>

            <div className="relative z-10 aspect-square w-full lg:absolute lg:left-1/2 lg:top-1/2 lg:w-[115%] lg:max-w-none lg:-translate-x-1/2 lg:-translate-y-1/2">
              {hasLottie ? (
                <HeroImages
                  desktopLottie={{
                    url: desktopLottieUrl,
                    loop,
                    autoplay,
                    type,
                  }}
                  mobileLottie={
                    mobileLottieUrl
                      ? {
                          url: mobileLottieUrl,
                          loop,
                          autoplay,
                          type,
                        }
                      : undefined
                  }
                  className="size-full"
                />
              ) : (
                <HeroImages
                  desktopImage={{
                    url: desktopImageUrl || '',
                    alt: featuredImage?.alt,
                    file: {
                      url: desktopImageUrl || '',
                      width: featuredImage?.file?.width,
                      height: featuredImage?.file?.height,
                    },
                  }}
                  mobileImage={
                    mobileImageUrl
                      ? {
                          url: mobileImageUrl,
                          alt: featuredImageMobile?.alt,
                          file: {
                            url: mobileImageUrl,
                            width: featuredImageMobile?.file?.width,
                            height: featuredImageMobile?.file?.height,
                          },
                        }
                      : undefined
                  }
                  className="size-full"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default HeroSplitLottie;
